import api from './api'

// Create a new form
export const createForm = async (formData) => {
  const response = await api.post('/forms', formData)
  return response.data
}

// Get a single form by ID
export const getForm = async (formId) => {
  const response = await api.get(`/forms/${formId}`)
  return response.data
}

// List all forms for the authenticated user (with pagination and search)
export const listForms = async ({ cursor, limit = 20, search = '', includeInactive = false, sortBy, sortOrder } = {}) => {
  const params = new URLSearchParams()
  if (cursor) params.append('cursor', JSON.stringify(cursor))
  if (limit) params.append('limit', limit.toString())
  if (search) params.append('search', search)
  if (includeInactive) params.append('includeInactive', 'true')
  if (sortBy) params.append('sortBy', sortBy)
  if (sortOrder) params.append('sortOrder', sortOrder)

  const response = await api.get(`/forms?${params.toString()}`)
  return response.data
}

// List nestable forms for the authenticated user.
// Backed by GET /forms?nestable=true (added in #246, Task 2). Returns the
// list of forms the current tenant has flagged as nestable and is the
// data source for the builder's nested-form picker.
export const listNestableForms = async () => {
  const response = await api.get('/forms?nestable=true')
  // GET /forms returns { items: [...], pagination: {...} }
  return response.data?.items || response.data?.forms || []
}

// Update an existing form
export const updateForm = async (formId, formData) => {
  const response = await api.put(`/forms/${formId}`, formData)
  return response.data
}

// Delete a form
export const deleteForm = async (formId) => {
  const response = await api.delete(`/forms/${formId}`)
  return response.data
}

// Duplicate a form
export const duplicateForm = async (formId, options = {}) => {
  const response = await api.post(`/forms/${formId}/duplicate`, options)
  return response.data
}

// Activate a form
export const activateForm = async (formId) => {
  const response = await api.post(`/forms/${formId}/activate`)
  return response.data
}

// Get public form schema (no auth required)
export const getPublicFormSchema = async (formId) => {
  const response = await api.get(`/public/forms/${formId}/schema`)
  return response.data
}

// Get public form with optional access code
// Returns { requiresAccessCode: true, formTitle, formId } if code required but not provided
export const getPublicForm = async (formIdOrSlug, accessCode = null) => {
  const config = accessCode ? { headers: { 'X-Access-Code': accessCode } } : {}
  const response = await api.get(`/public/forms/${formIdOrSlug}`, config)
  return response.data
}

// Pull prefill content from the form's configured external API (#440).
// `query` (plain object) is forwarded as query params so the external API
// can identify the caller (e.g. { email: 'a@b.com' }). `accessCode` mirrors
// getPublicForm's X-Access-Code header — required so an access-code-gated
// public form's content source isn't reachable without the verified code.
// `timeoutMs` bounds the request client-side so a slow/misconfigured tenant
// API can't hold the form viewer's initial-submission wait open indefinitely.
export const getFormContentSource = async (formIdOrSlug, { isPublic = false, query = {}, accessCode = null, timeoutMs } = {}) => {
  const params = new URLSearchParams(query)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const path = isPublic ? `/public/forms/${formIdOrSlug}/content-source` : `/forms/${formIdOrSlug}/content-source`
  const config = {}
  if (accessCode) config.headers = { 'X-Access-Code': accessCode }
  if (timeoutMs) config.timeout = timeoutMs
  const response = await api.get(`${path}${qs}`, config)
  return response.data
}

// Verify access code for a public form
export const verifyAccessCode = async (formIdOrSlug, code) => {
  const response = await api.post(`/public/forms/${formIdOrSlug}/verify-access`, { code })
  return response.data
}

// Request an email verification code for a public form (email-verification bot protection)
export const requestVerificationCode = async (formIdOrSlug, email) => {
  const response = await api.post(`/public/forms/${formIdOrSlug}/request-verification-code`, { email })
  return response.data
}

// List form versions
export const listFormVersions = async (formId) => {
  const response = await api.get(`/forms/${formId}/versions`)
  return response.data
}

// Get a specific form version
export const getFormVersion = async (formId, version) => {
  const response = await api.get(`/forms/${formId}/versions/${version}`)
  return response.data
}

// Get a nested-form schema for the renderer (issue #246, Task 19).
//
// When `version` is omitted, returns the form's current published schema
// (uses `getForm` which hits GET /forms/{formId}). When `version` is
// supplied, returns the snapshot for that version (uses `getFormVersion`
// which hits GET /forms/{formId}/versions/{version}). The renderer's
// NestedFormResolver uses this to fetch each child schema referenced by
// the parent form before handing the inlined schema to Form.io.
//
// Returns the schema-shaped portion only (i.e. the object with a
// `components` array). The current-version response normalises to
// `formSchema` (so we read that field); the versioned response returns
// the schema directly.
export const getFormSchema = async (formId, version) => {
  if (version === undefined || version === null || version === '') {
    const form = await getForm(formId)
    return form?.formSchema || form?.schema || form
  }
  const v = await getFormVersion(formId, version)
  return v?.formSchema || v?.schema || v
}

// Bulk status change (activate/deactivate multiple forms)
export const bulkStatus = async (action, payload, tenantId = null) => {
  const url = tenantId ? `/forms/bulk-status?tenantId=${tenantId}` : '/forms/bulk-status'
  const response = await api.post(url, { action, ...payload })
  return response.data
}
